"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CookieConsentResponse = exports.ConsentStatusResponse = exports.ConsentRecord = exports.LegalDocumentsResponse = exports.LegalDocument = exports.DeleteAccountBody = exports.CookieConsentBody = exports.RecordConsentBody = exports.CookieCategories = exports.CookieConsentSource = exports.LegalDocumentKind = exports.LegalDocumentType = void 0;
/**
 * SOLID Review
 * S — Strong. One concern: the wire shapes of the /api/legal surface.
 * O — Strong. A new consent category or endpoint is an additive schema change here.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. Per-endpoint schemas; the cookie-consent route cannot reach the account-deletion body.
 * D — Strong. zod only.
 */
/**
 * DDD Layer: none — shared wire contract.
 *
 * The client half of backend/src/framework/legal/routes/legalRoutes.ts and the server half of
 * frontend/src/infrastructure/legal/LegalApiAdapter.ts + infrastructure/consent/ConsentApiAdapter.ts.
 *
 * This surface is the strongest argument for the whole exercise: it is GDPR machinery, the routes
 * validate it with 30 lines of hand-rolled checking (`isFiveBooleans`, a `validTypes` array, three
 * separate 400 branches), and the frontend re-declares every shape. Every one of those checks is a
 * sentence a schema states once.
 */
const zod_1 = require("zod");
/** The two documents a visitor can consent to. Mirrors LegalDocumentType on the server. */
exports.LegalDocumentType = zod_1.z.enum(['PRIVACY_POLICY', 'TERMS_OF_SERVICE']);
/**
 * Every document the server PUBLISHES — the two above plus the Security/Data Impact assessment and the
 * Cookie Policy (`LegalDocumentProvider.getAllDocuments`, and the backend's own `LegalDocumentTypeSchema`).
 *
 * ADDED 2026-10-04 (frontend strict-ports plan, FE4a; handbook §28.201). `LegalDocument.type` used the
 * two-value consent enum above, so `GET /documents` — which has always answered four documents — failed
 * this contract on every call. The client parses against it (since 2026-08-30), so in production every
 * tab of the legal modal showed an error and the re-acceptance gate, which fails open, never engaged.
 * Measured against the live endpoint before the fix: four documents, items 2 and 3 refused. Consents
 * still take only the two: you accept Terms and Privacy, you read all four.
 */
exports.LegalDocumentKind = zod_1.z.enum(['PRIVACY_POLICY', 'TERMS_OF_SERVICE', 'SECURITY_DATA_IMPACT', 'COOKIE_POLICY']);
/** Where a cookie choice came from. Mirrors VALID_COOKIE_SOURCES. */
exports.CookieConsentSource = zod_1.z.enum(['TCF', 'US', 'MANUAL']);
/**
 * EXACTLY five booleans, all required.
 *
 * The route enforces this by looping a `keys` tuple and rejecting on the first non-boolean, then
 * rebuilding the object with five `as boolean` casts. Both the loop and the casts are what a schema
 * removes: `.strict()` additionally rejects a SIXTH category, which the hand-rolled version silently
 * accepted and dropped — a client sending a category the server does not know would have believed it
 * was recorded.
 */
exports.CookieCategories = zod_1.z.object({
    adStorage: zod_1.z.boolean(),
    adUserData: zod_1.z.boolean(),
    adPersonalization: zod_1.z.boolean(),
    analyticsStorage: zod_1.z.boolean(),
    functionality: zod_1.z.boolean(),
}).strict();
// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------
exports.RecordConsentBody = zod_1.z.object({
    documentType: exports.LegalDocumentType,
    documentVersion: zod_1.z.string().trim().min(1),
});
exports.CookieConsentBody = zod_1.z.object({
    consentId: zod_1.z.string().trim().min(1),
    categories: exports.CookieCategories,
    tcString: zod_1.z.string().nullable().optional(),
    source: exports.CookieConsentSource,
    region: zod_1.z.string().trim().min(1),
    policyVersion: zod_1.z.string().trim().min(1),
});
/** DELETE /me — the emailed confirmation code, same 4-digit shape as the auth codes. */
exports.DeleteAccountBody = zod_1.z.object({
    code: zod_1.z.string().trim().regex(/^\d{4}$/, 'must be four digits'),
});
// ---------------------------------------------------------------------------
// Responses
// ---------------------------------------------------------------------------
exports.LegalDocument = zod_1.z.object({
    type: exports.LegalDocumentKind,
    version: zod_1.z.string(),
    title: zod_1.z.string(),
    content: zod_1.z.string(),
});
/** GET /documents returns the ARRAY itself, not `{ documents: [...] }` — the route unwraps it. */
exports.LegalDocumentsResponse = zod_1.z.array(exports.LegalDocument);
exports.ConsentRecord = zod_1.z.object({
    documentType: exports.LegalDocumentType,
    documentVersion: zod_1.z.string(),
    consentedAt: zod_1.z.string(),
});
/**
 * CORRECTED 2026-08-30. This declared `{ privacyPolicy, termsOfService }` — a shape NEITHER SIDE
 * has ever used.
 *
 * The route returns `GetConsentStatusUseCase`'s IEntityConsentStatus verbatim
 * (`res.status(200).json(result.value)`), which is the three booleans below. The frontend's
 * LegalApiAdapter CAST the response to its own `IEntityConsent` — also the three booleans — so both
 * ends agreed with each other and the contract agreed with neither.
 *
 * Nothing caught it because nothing parsed against it: the server does not validate its responses,
 * the client cast instead of parsing, and this file's own tests asserted only that the schema
 * accepts the shape the schema itself declared. A self-consistent fiction, published in
 * openapi.json, which §28.74 names as the thing that settles client/server agreement.
 *
 * It is load-bearing now — LegalApiAdapter parses with it.
 */
exports.ConsentStatusResponse = zod_1.z.object({
    hasTerms: zod_1.z.boolean(),
    hasPrivacy: zod_1.z.boolean(),
    allCurrent: zod_1.z.boolean(),
});
/**
 * CORRECTED 2026-08-30, the SECOND schema in this file found describing a response nobody sends.
 *
 * It declared `{ consentId, recordedAt }`. The route returns the use case's IEntityCookieConsent
 * verbatim (`res.status(201).json(result.value)`) — the ten fields below — and answers **201**, not
 * 200. `recordedAt` does not exist anywhere in the system; the field is `consentedAt`, and it is
 * serialised from a Date.
 *
 * Found the same way as ConsentStatusResponse: nothing parsed against it. ConsentApiAdapter posted
 * and ignored the body entirely, so the schema, the OpenAPI document generated from it, and the
 * server were free to disagree indefinitely. It is load-bearing now.
 */
exports.CookieConsentResponse = zod_1.z.object({
    id: zod_1.z.string(),
    playerId: zod_1.z.string().nullable(),
    consentId: zod_1.z.string(),
    categories: exports.CookieCategories,
    tcString: zod_1.z.string().nullable(),
    source: exports.CookieConsentSource,
    region: zod_1.z.string(),
    policyVersion: zod_1.z.string(),
    consentedAt: zod_1.z.string(),
});
//# sourceMappingURL=legal.js.map